'use client';

import React from 'react';
import { Dialog as ChakraDialog, useDisclosure, type UseDisclosureReturn } from '@chakra-ui/react';

import { Button } from './button';
import { Box } from './box';
import { Text } from './text';

/**
 * Custom Dialog.Content wrapper with automatic Box background
 * RULE 15: DRY compliance - single source of truth for modal styling
 * Prevents transparent modal backgrounds across the entire application
 */
const DialogContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof ChakraDialog.Content>
>((props, ref) => {
  const { children, ...rest } = props;
  return (
    <ChakraDialog.Content ref={ref} {...rest}>
      <Box background="bg.default" borderRadius="lg" boxShadow="lg">
        {children}
      </Box>
    </ChakraDialog.Content>
  );
});

DialogContent.displayName = 'DialogContent';

/**
 * Enhanced Dialog object with custom Content component
 * All other Dialog components pass through from Chakra UI
 */
export const Dialog = {
  Root: ChakraDialog.Root,
  Trigger: ChakraDialog.Trigger,
  Content: DialogContent, // Custom wrapped version
  Header: ChakraDialog.Header,
  Body: ChakraDialog.Body,
  Footer: ChakraDialog.Footer,
  CloseTrigger: ChakraDialog.CloseTrigger,
  Backdrop: ChakraDialog.Backdrop,
  Positioner: ChakraDialog.Positioner,
  Title: ChakraDialog.Title,
  Description: ChakraDialog.Description,
  ActionTrigger: ChakraDialog.ActionTrigger,
} as const;

export interface TmsModalProps {
  trigger?: React.ReactNode;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
  showCloseButton?: boolean;
  preventClose?: boolean;
  disclosure?: UseDisclosureReturn;
}

export function Modal({
  trigger,
  title,
  description,
  children,
  footer,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDestructive = false,
  showCloseButton = true,
  preventClose = false,
  disclosure,
}: TmsModalProps) {
  const internalDisclosure = useDisclosure();
  const { open, onOpen, onClose } = disclosure || internalDisclosure;
  const [isLoading, setIsLoading] = React.useState(false);

  const handleClose = () => {
    if (!preventClose && !isLoading) {
      onCancel?.();
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (onConfirm) {
      setIsLoading(true);
      try {
        await onConfirm();
        if (!preventClose) {
          onClose();
        }
      } catch (error) {
        // Error handling is done by the onConfirm function
        console.error('Modal confirm error:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      {trigger && (
        <Box onClick={onOpen} display="inline-block">
          {trigger}
        </Box>
      )}

      <Dialog.Root
        open={open}
        onOpenChange={(e: { open: boolean }) => (e.open ? onOpen() : handleClose())}
        size="md"
        placement="center"
        closeOnInteractOutside={!preventClose && !isLoading}
        closeOnEscape={!preventClose && !isLoading}
        trapFocus
        initialFocusEl={undefined}
        finalFocusEl={undefined}
        preventScroll
        lazyMount
        unmountOnExit
      >
        <Dialog.Backdrop />
        <Dialog.Content {...(description ? { 'aria-describedby': 'modal-description' } : {})}>
          <Box background="bg.default" borderRadius="lg" boxShadow="lg">
            {(title || showCloseButton) && (
              <Dialog.Header>
                {title}
                {showCloseButton && !preventClose && (
                  <Dialog.CloseTrigger aria-label="Close dialog" />
                )}
              </Dialog.Header>
            )}

            <Dialog.Body>
              {description && (
                <Text id="modal-description" marginBottom={4} color="#6B7280">
                  {description}
                </Text>
              )}
              {children}
            </Dialog.Body>

            {(footer || onConfirm || onCancel) && (
              <Dialog.Footer>
                {footer || (
                  <>
                    {onCancel && (
                      <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isLoading}
                        {...({ marginRight: 3 } as Record<string, unknown>)}
                      >
                        {cancelText}
                      </Button>
                    )}
                    {onConfirm && (
                      <Button
                        variant="primary"
                        colorPalette={isDestructive ? 'red' : 'blue'}
                        onClick={handleConfirm}
                        isLoading={isLoading}
                        loadingText="Loading..."
                      >
                        {confirmText}
                      </Button>
                    )}
                  </>
                )}
              </Dialog.Footer>
            )}
          </Box>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}

// Compound components for more control
export const ModalRoot = Dialog.Root;
export const ModalTrigger = Dialog.Trigger;
export const ModalContent = Dialog.Content;
export const ModalHeader = Dialog.Header;
export const ModalBody = Dialog.Body;
export const ModalFooter = Dialog.Footer;
export const ModalCloseTrigger = Dialog.CloseTrigger;
export const ModalBackdrop = Dialog.Backdrop;

// Create namespace for dot notation
Modal.Content = Dialog.Content;
Modal.Header = Dialog.Header;
Modal.Body = Dialog.Body;
Modal.Footer = Dialog.Footer;
Modal.CloseTrigger = Dialog.CloseTrigger;
Modal.Backdrop = Dialog.Backdrop;
Modal.Root = Dialog.Root;

// Confirmation Modal variant for destructive actions
// Uses Dialog.Root internally - Chakra UI v3 compliant
export interface ConfirmModalProps extends Omit<TmsModalProps, 'onConfirm'> {
  onConfirm: () => void | Promise<void>;
}

export function ConfirmModal({
  title = 'Are you sure?',
  description = 'This action cannot be undone.',
  confirmText = 'Delete',
  isDestructive = true,
  disclosure,
  trigger,
  ...props
}: ConfirmModalProps) {
  const internalDisclosure = useDisclosure();
  const { open, onOpen, onClose } = disclosure || internalDisclosure;

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(e: { open: boolean }) => (e.open ? onOpen() : onClose())}
    >
      {trigger && (
        <Dialog.Trigger
          asChild
          {...({} as Record<string, unknown> & { children?: React.ReactNode })}
        >
          {trigger}
        </Dialog.Trigger>
      )}
      <Dialog.Backdrop />
      <Dialog.Content {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
        {title && (
          <Dialog.Header>
            {title}
            <Dialog.CloseTrigger />
          </Dialog.Header>
        )}
        <Dialog.Body>
          {description && (
            <Text color="#6B7280" marginBottom={4}>
              {description}
            </Text>
          )}
        </Dialog.Body>
        <Dialog.Footer>
          <Button
            variant="outline"
            onClick={() => {
              props.onCancel?.();
              onClose();
            }}
          >
            {props.cancelText || 'Cancel'}
          </Button>
          <Button
            variant={isDestructive ? 'danger' : 'primary'}
            colorPalette={isDestructive ? 'red' : 'blue'}
            onClick={() => {
              props.onConfirm();
              onClose();
            }}
          >
            {confirmText}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
}

// useModal hook for programmatic control
export function useModal() {
  const disclosure = useDisclosure();
  const [modalProps, setModalProps] = React.useState<Partial<TmsModalProps>>({});

  const openModal = (props: Partial<TmsModalProps> = {}) => {
    setModalProps(props);
    disclosure.onOpen();
  };

  const closeModal = () => {
    disclosure.onClose();
    // Reset props after animation
    setTimeout(() => setModalProps({}), 300);
  };

  const modal = (
    <Dialog.Root
      open={disclosure.open}
      onOpenChange={(e: { open: boolean }) => disclosure.setOpen(e.open)}
    >
      <Dialog.Backdrop />
      <Dialog.Content {...({} as Record<string, unknown> & { children?: React.ReactNode })}>
        {modalProps.title && (
          <Dialog.Header>
            {modalProps.title}
            <Dialog.CloseTrigger />
          </Dialog.Header>
        )}
        <Dialog.Body>
          {modalProps.description && (
            <Text color="#6B7280" marginBottom={4}>
              {modalProps.description}
            </Text>
          )}
          {modalProps.children}
        </Dialog.Body>
        {(modalProps.footer || modalProps.onConfirm || modalProps.onCancel) && (
          <Dialog.Footer>
            {modalProps.footer || (
              <>
                {modalProps.onCancel && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      modalProps.onCancel?.();
                      closeModal();
                    }}
                  >
                    {modalProps.cancelText || 'Cancel'}
                  </Button>
                )}
                {modalProps.onConfirm && (
                  <Button
                    variant={modalProps.isDestructive ? 'danger' : 'primary'}
                    colorPalette={modalProps.isDestructive ? 'red' : 'blue'}
                    onClick={async () => {
                      await modalProps.onConfirm?.();
                      closeModal();
                    }}
                  >
                    {modalProps.confirmText || 'Confirm'}
                  </Button>
                )}
              </>
            )}
          </Dialog.Footer>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );

  return {
    modal,
    openModal,
    closeModal,
    isOpen: disclosure.open,
  };
}
